/**
 * The registrable domains this product owns. Only these may carry a `Domain` attribute,
 * which is what shares the session between a tenant subdomain (`casais.bite-club.app`)
 * and the apex that serves the app. Everything else stays host-only: a `Domain` naming a
 * public suffix (`.vercel.app`, `.co.uk`) or a platform nobody here controls
 * (`.onrender.com`) is rejected by the browser, and the cookie is silently dropped along
 * with the sign-in. Guessing from the last two labels cannot tell those apart, so an
 * explicit list it is.
 */
const OWNED_DOMAINS = ['bite-club.app'];

/**
 * The `Domain` attribute the Supabase auth cookies are written with.
 *
 * This must be identical everywhere the cookies are written — the browser client, the
 * proxy, and server actions — because a `Set-Cookie` whose `Domain` does not match the
 * one a cookie was created with cannot overwrite or delete it. It creates a second,
 * host-only cookie of the same name instead. That is how a sign-out can appear to
 * succeed while the original session cookie stays on disk.
 *
 * Returns `undefined` to mean "no Domain attribute", i.e. a host-only cookie. That is
 * the safe default: it always works, and is only insufficient when the session has to
 * be shared across subdomains of an owned domain.
 */
export function getCookieDomainForHostname(hostname: string): string | undefined {
  if (!hostname) return undefined;

  const host = stripPort(hostname);
  if (!host) return undefined;

  // IPv6 literals cannot carry a Domain attribute at all.
  if (host.startsWith('[')) return undefined;

  // Local subdomain testing (tech.localhost) predates this module and is kept as it was.
  // Verified in Chromium: on `localhost` this attribute is stored as host-only, and on
  // `*.localhost` the cookie is rejected outright, so Chromium never shares a session
  // between the two. Production hosts never take this branch.
  if (host.endsWith('localhost')) {
    return 'localhost';
  }

  // An IP literal cannot carry a Domain attribute either: browsers reject one whose
  // value is an address, which would drop the cookie outright.
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)) {
    return undefined;
  }

  const parts = host.split('.');
  if (parts.length > 1) {
    const registrable = parts.slice(-2).join('.');
    // Only a domain we own may be shared across its subdomains. Returning the host for
    // anything else would still emit `Domain=<host>`, which RFC 6265 also matches
    // against every subdomain of it; host-only means omitting the attribute entirely.
    if (OWNED_DOMAINS.includes(registrable)) {
      return `.${registrable}`;
    }
  }

  // Not ours, or a single-label host with nothing to scope to: host-only.
  return undefined;
}

/**
 * Removes the port a `Host` header carries, which a `Domain` attribute must not have.
 *
 * IPv6 hosts arrive bracketed (`[::1]:3000`), so splitting on the first colon would
 * truncate them to `[`.
 */
function stripPort(hostname: string): string {
  if (hostname.startsWith('[')) {
    const closingBracket = hostname.indexOf(']');
    return closingBracket === -1 ? hostname : hostname.slice(0, closingBracket + 1);
  }
  return hostname.split(':')[0];
}
